// src/features/dashboard/components/LocationDropdown.tsx

import {
  useEffect,
  useRef,
  useState,
} from 'react';

interface LocationItem {
  geo: string;
}

interface Props {
  locations: LocationItem[];
  value: string;
  onChange: (geo: string) => void;
}

const LocationDropdown = ({
  locations,
  value,
  onChange,
}: Props) => {
  const [open, setOpen] =
    useState(false);

  const [search, setSearch] =
    useState('');

  const [selected, setSelected] =
    useState('All Locations');

  const dropdownRef =
    useRef<HTMLDivElement>(null);

  /**
   * Close dropdown outside click
   */
  useEffect(() => {
    const handleClickOutside = (
      event: MouseEvent,
    ) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(
          event.target as Node,
        )
      ) {
        setOpen(false);
      }
    };

    document.addEventListener(
      'mousedown',
      handleClickOutside,
    );

    return () => {
      document.removeEventListener(
        'mousedown',
        handleClickOutside,
      );
    };
  }, []);

  /**
   * Update selected location
   */
  useEffect(() => {
    if (!value) {
      setSelected('All Locations');
      return;
    }

    setSelected(value);
  }, [value]);

  /**
   * Search locations
   */
  const filteredLocations =
    locations.filter((location) =>
      location.geo
        ?.toLowerCase()
        .includes(
          search.toLowerCase().trim(),
        ),
    );

  /**
   * Select location
   */
  const handleSelect = (
    geo: string,
  ) => {
    onChange(geo);

    setSearch('');
    setOpen(false);
  };

  /**
   * Select all locations
   */
  const handleSelectAll = () => {
    onChange('');

    setSelected(
      'All Locations',
    );

    setSearch('');
    setOpen(false);
  };

  return (
    <div
      ref={dropdownRef}
      className="relative min-w-[170px]"
    >
      {/* Selected value */}
      <button
        type="button"
        onClick={() =>
          setOpen(
            (previous) => !previous,
          )
        }
        className="
          flex
          h-[38px]
          w-full
          items-center
          justify-between
          rounded-[10px]
          border
          border-[#D0D5DD]
          bg-white
          px-3
          text-[11px]
          font-medium
          text-[#111827]
          outline-none
        "
      >
        <span className="truncate">
          {selected}
        </span>

        <span
          className="
            ml-2
            text-[10px]
            text-[#667085]
          "
        >
          ▼
        </span>
      </button>

      {open && (
        <div
          className="
            absolute
            left-0
            top-[42px]
            z-50
            w-full
            rounded-[12px]
            border
            border-[#D0D5DD]
            bg-white
            shadow-lg
          "
        >
          {/* Search */}
          <div className="p-2">
            <input
              type="text"
              placeholder="Search location..."
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value,
                )
              }
              onClick={(event) =>
                event.stopPropagation()
              }
              className="
                h-[34px]
                w-full
                rounded-[8px]
                border
                border-[#84CAFF]
                px-3
                text-[11px]
                outline-none
              "
            />
          </div>

          {/* All Locations */}
          <div className="px-2 pb-2">
            <button
              type="button"
              onClick={
                handleSelectAll
              }
              className="
                w-full
                rounded-[8px]
                bg-[#EEF2FF]
                px-3
                py-2
                text-left
                text-[12px]
                font-medium
                text-[#111827]
                hover:bg-[#E0E7FF]
              "
            >
              All Locations
            </button>
          </div>

          {/* Locations */}
          <div
            className="
              max-h-[170px]
              overflow-y-auto
              pb-2
            "
          >
            {filteredLocations.length ===
            0 ? (
              <div
                className="
                  px-3
                  py-3
                  text-[11px]
                  text-[#667085]
                "
              >
                No location found
              </div>
            ) : (
              filteredLocations.map(
                (location) => {
                  const isSelected =
                    location.geo ===
                    value;

                  return (
                    <button
                      type="button"
                      key={
                        location.geo
                      }
                      onClick={() =>
                        handleSelect(
                          location.geo,
                        )
                      }
                      className={`
                        flex
                        w-full
                        items-center
                        px-3
                        py-2
                        text-left
                        text-[12px]
                        text-[#111827]
                        hover:bg-[#F5F7FA]
                        ${
                          isSelected
                            ? 'bg-[#EEF2FF] font-semibold'
                            : ''
                        }
                      `}
                    >
                      {location.geo}
                    </button>
                  );
                },
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationDropdown;